import React from 'react';
import {
  Animated,
  Image,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import RelicSprite from '../shared/RelicSprite';
import ShimmerHalo from '../shared/ShimmerHalo';
import { CheckIcon, CloseIcon, CoinIcon } from '../shared/RelicVaultIcons';
import { FLY_EASING, useOneShot, usePopIn } from '../shared/useRelicVaultAnimations';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import {
  COLORS,
  accentButton,
  bodyFont,
  em,
  headingFont,
  modalBackdrop,
  modalSurface,
} from '../shared/theme';
import { getRelic, getGuardian, guildLabelFor, rarityColorFor } from 'utils/relicVault/catalog';
import { shade } from 'utils/relicVault/colorUtils';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

import { TYPE } from 'style/typography';

const CARD_WIDTH = 430;
const CONFIRM_ICON_SIZE = 190; // 160px icon at the design's 118.75%

/**
 * Store purchase popup: relic confirm (with the icon flying into the vault),
 * companion "Meet <name>" reveal, and the shared success stage.
 */
const PurchaseModal = ({
  catalog,
  flow,
  balance,
  ownedCompanionIds,
  equippedCompanionId,
}) => {
  const { activeItem, stage } = flow;
  const { width, height, isPhone } = useRelicVaultLayout();
  if (!activeItem || !stage) return null;

  const relic = activeItem.kind === 'relic' ? getRelic(catalog, activeItem.id) : null;
  const companion =
    activeItem.kind === 'companion' ? getGuardian(catalog, activeItem.id) : null;
  if (!relic && !companion) return null;

  const badgeColor = relic ? rarityColorFor(catalog, relic) : companion.colorHex;
  const title = relic ? relic.name : companion.name;
  const cardWidth = isPhone ? width * 0.92 : Math.min(CARD_WIDTH, width - 48);

  return (
    <View style={[StyleSheet.absoluteFill, styles.backdrop, modalBackdrop]}>
      <TouchableOpacity
        activeOpacity={1}
        style={StyleSheet.absoluteFill}
        onPress={flow.close}
        accessibilityLabel="Close"
      />
      <View
        style={[
          styles.card,
          modalSurface,
          {
            width: cardWidth,
            maxHeight: height * 0.9,
            borderColor: badgeColor,
          },
        ]}>
        <ScrollView
          bounces={false}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={
            companion
              ? null
              : [styles.cardPadding, isPhone && styles.cardPaddingPhone]
          }>
          {stage === 'confirm' && relic && (
            <RelicConfirm catalog={catalog} relic={relic} color={badgeColor} flow={flow} isPhone={isPhone} />
          )}
          {stage === 'confirm' && companion && (
            <CompanionReveal
              companion={companion}
              balance={balance}
              owned={ownedCompanionIds.includes(companion.guildId)}
              equipped={equippedCompanionId === companion.guildId}
              flow={flow}
            />
          )}
          {stage === 'success' && (
            <SuccessStage
              title={title}
              successText={relic ? 'Added to Vault!' : 'Companion Unlocked!'}
              padded={!!companion}
              onDone={flow.close}
            />
          )}
        </ScrollView>

        {stage === 'confirm' && (
          <TouchableOpacity
            onPress={flow.close}
            accessibilityRole="button"
            accessibilityLabel="Close"
            style={[styles.closeBtn, companion && styles.closeBtnOnArt]}>
            <CloseIcon />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const RelicConfirm = ({ catalog, relic, color, flow, isPhone }) => {
  const fly = useOneShot(flow.animating, 650, FLY_EASING);
  const flyStops = [0, 0.55, 1];

  return (
    <>
      <View
        style={[
          styles.icon,
          // Bleed to the card edges through whichever padding is active.
          isPhone && styles.iconPhone,
          { backgroundColor: shade(color, 0.4) },
          webOnly({
            backgroundImage: `radial-gradient(circle at 50% 38%, ${color}50 0%, transparent 62%), linear-gradient(165deg, ${shade(color, 0.4)} 0%, #110E28 88%)`,
          }),
        ]}>
        <Animated.View
          style={{
            opacity: fly.interpolate({ inputRange: flyStops, outputRange: [1, 0.85, 0] }),
            transform: [
              { translateY: fly.interpolate({ inputRange: flyStops, outputRange: [0, 70, 230] }) },
              { scale: fly.interpolate({ inputRange: flyStops, outputRange: [1, 0.62, 0.1] }) },
              {
                rotate: fly.interpolate({
                  inputRange: flyStops,
                  outputRange: ['0deg', '-10deg', '18deg'],
                }),
              },
            ],
          }}>
          <RelicSprite relic={relic} grid={catalog.sheetGrid} size={CONFIRM_ICON_SIZE} shadow="rgba(0,0,0,0.4)" />
        </Animated.View>
        <ShimmerHalo glow={`${color}80`} replayKey={relic.relicId} />
      </View>

      <View style={styles.metaLine}>
        <Text style={styles.metaText}>{guildLabelFor(catalog, relic)}</Text>
        <Text style={styles.metaDot}>{'•'}</Text>
        <View style={[styles.inlineBadge, { borderColor: color }]}>
          <Text style={[styles.inlineBadgeText, { color }]}>{catalog.tierByKey.get(relic.rarity)?.label}</Text>
        </View>
      </View>
      <Text style={styles.title}>{relic.name}</Text>
      <Text style={styles.desc}>{relic.description}</Text>

      {!!flow.error && <Text style={styles.error}>{flow.error}</Text>}

      <View style={styles.actions}>
        <TouchableOpacity
          activeOpacity={0.9}
          onPress={flow.close}
          style={[styles.modalBtn, styles.cancelBtn]}>
          <Text style={[styles.modalBtnText, { color: COLORS.lilacLight }]}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.9}
          disabled={flow.animating}
          onPress={flow.confirm}
          style={[
            styles.modalBtn,
            styles.confirmBtnSpacing,
            accentButton('0 6px 20px rgba(139,92,246,0.5)'),
          ]}>
          <CoinIcon size={17} light />
          <Text style={[styles.modalBtnText, styles.btnTextAfterIcon]}>
            {relic.price} Purchase
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );
};

const CompanionReveal = ({ companion, balance, owned, equipped, flow }) => {
  const shortfall = companion.price - (balance ?? 0);
  const canAfford = shortfall <= 0;

  return (
    <View style={styles.introPhoto}>
      <Image
        source={{ uri: companion.introUrl }}
        resizeMode="cover"
        style={StyleSheet.absoluteFill}
      />
      <View
        style={[
          styles.introFooter,
          IS_WEB
            ? {
                backgroundImage:
                  'linear-gradient(to top, rgba(9,7,18,0.94) 0%, rgba(9,7,18,0.8) 55%, rgba(9,7,18,0) 100%)',
              }
            : styles.introFooterNative,
        ]}>
        {/* The footer gradient only covers one row, so owned companions show a
            single element: the equip action, or the equipped status. */}
        {owned && equipped && (
          <View style={styles.introStatus}>
            <CheckIcon size={12} />
            <Text style={styles.introStatusText}>Equipped</Text>
          </View>
        )}
        {owned && !equipped && (
          <TouchableOpacity
            activeOpacity={0.9}
            onPress={() => flow.equip(companion.guildId)}
            style={[
              styles.modalBtn,
              styles.introBuy,
              accentButton('0 6px 20px rgba(139,92,246,0.5)'),
            ]}>
            <Text style={styles.modalBtnText}>Equip {companion.name}</Text>
          </TouchableOpacity>
        )}
        {!owned && (
          <>
            {!!flow.error && <Text style={styles.error}>{flow.error}</Text>}
            <TouchableOpacity
              activeOpacity={0.9}
              disabled={!canAfford || flow.animating}
              onPress={flow.confirm}
              style={[
                styles.modalBtn,
                styles.introBuy,
                accentButton('0 6px 20px rgba(139,92,246,0.5)'),
                !canAfford && styles.introBuyDisabled,
              ]}>
              <CoinIcon size={17} light />
              <Text style={[styles.modalBtnText, styles.btnTextAfterIcon]}>
                {companion.price} Purchase
              </Text>
            </TouchableOpacity>
            {!canAfford && (
              <Text style={styles.affordNote}>Need {shortfall} more QuikCoins</Text>
            )}
          </>
        )}
      </View>
    </View>
  );
};

const SuccessStage = ({ title, successText, padded, onDone }) => {
  const pop = usePopIn(successText);
  return (
    <View style={padded && styles.successPadded}>
      <Animated.View style={[styles.successIcon, pop]}>
        <CheckIcon size={40} strokeWidth="2.6" />
      </Animated.View>
      <Text style={styles.successTitle}>{successText}</Text>
      <Text style={styles.successSub}>{title} is now yours</Text>
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={onDone}
        style={[styles.modalBtn, accentButton('0 6px 20px rgba(139,92,246,0.5)')]}>
        <Text style={styles.modalBtnText}>Nice!</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    borderWidth: 2,
    borderRadius: 24,
    overflow: 'hidden',
  },
  cardPadding: {
    paddingTop: 36,
    paddingHorizontal: 28,
    paddingBottom: 28,
  },
  cardPaddingPhone: {
    paddingTop: 26,
    paddingHorizontal: 18,
    paddingBottom: 18,
  },
  closeBtn: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  closeBtnOnArt: {
    backgroundColor: 'rgba(10,8,20,0.55)',
  },
  icon: {
    height: 300,
    marginTop: -36,
    marginHorizontal: -28,
    marginBottom: 20,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  iconPhone: {
    marginTop: -26,
    marginHorizontal: -18,
  },
  metaLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
  },
  metaText: {
    ...bodyFont('600'),
    fontSize: TYPE.sm,
    color: COLORS.muted,
  },
  metaDot: {
    ...bodyFont('700'),
    fontSize: TYPE.sm,
    color: COLORS.dim,
    marginHorizontal: 8,
  },
  inlineBadge: {
    borderWidth: 2,
    borderRadius: 999,
    paddingVertical: 3,
    paddingHorizontal: 11,
  },
  inlineBadgeText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.05, 10.5),
    textTransform: 'uppercase',
  },
  title: {
    ...headingFont('800'),
    fontSize: TYPE['2xl'],
    marginBottom: 6,
    color: COLORS.textWarm,
    textAlign: 'center',
  },
  desc: {
    ...bodyFont('400'),
    fontSize: TYPE.sm,
    lineHeight: 20.8,
    marginTop: -8,
    marginBottom: 20,
    color: COLORS.lilacSoft,
    textAlign: 'center',
  },
  error: {
    ...bodyFont('700'),
    fontSize: TYPE.xs,
    color: COLORS.red,
    textAlign: 'center',
    marginBottom: 12,
  },
  actions: {
    flexDirection: 'row',
  },
  modalBtn: {
    flex: 1,
    minHeight: 48,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtn: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  confirmBtnSpacing: {
    marginLeft: 12,
  },
  modalBtnText: {
    ...headingFont('700'),
    fontSize: TYPE.sm,
    letterSpacing: em(0.03, 13),
    textTransform: 'uppercase',
    color: '#FFFFFF',
  },
  btnTextAfterIcon: {
    marginLeft: 6,
  },
  introPhoto: {
    width: '100%',
    aspectRatio: 2 / 3,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    overflow: 'hidden',
  },
  introFooter: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: 14,
    paddingHorizontal: 26,
    paddingBottom: 12,
    alignItems: 'center',
  },
  introFooterNative: {
    backgroundColor: 'rgba(9,7,18,0.85)',
  },
  introStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: 'rgba(74,222,128,0.14)',
    borderWidth: 1.5,
    borderColor: 'rgba(74,222,128,0.4)',
  },
  introStatusText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    color: COLORS.green,
    marginLeft: 6,
  },
  introBuy: {
    flex: 0,
    alignSelf: 'stretch',
    minHeight: 42,
    marginTop: 8,
  },
  introBuyDisabled: {
    opacity: 0.5,
  },
  affordNote: {
    ...bodyFont('600'),
    fontSize: TYPE.xs,
    color: COLORS.muted,
    marginTop: 8,
  },
  successPadded: {
    paddingTop: 34,
    paddingHorizontal: 28,
    paddingBottom: 28,
  },
  successIcon: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: 'rgba(74,222,128,0.15)',
    borderWidth: 2,
    borderColor: COLORS.green,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: 18,
  },
  successTitle: {
    ...headingFont('800'),
    fontSize: TYPE['2xl'],
    color: COLORS.green,
    marginBottom: 8,
    textAlign: 'center',
  },
  successSub: {
    ...bodyFont('600'),
    fontSize: TYPE.sm,
    color: COLORS.lilacSoft,
    marginBottom: 22,
    textAlign: 'center',
  },
});

export default PurchaseModal;
